<template>
  <transition name="fade" appear>
    <!-- Binding to keyup/keydown (instead of keypress) on purpose. It prevents the scrollToNextSection from being interrupted -->
    <form class="rf-form" :class="{'rf-form-busy': state.busy }" novalidate @submit.prevent="onSubmit" @keydown.enter="_onEnterKey(null, $event)" @keyup.enter.stop.prevent="_onEnterKey(null, $event)" ref="form">

      <!-- Busy spinner -->
      <!-- Adapted from http://tobiasahlin.com/spinkit/ -->
      <div class="rf-form-busy-mask">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>

      <div class="rf-form-body">
        <!-- Pass reference to entire component instance so we can use all the methods from the parent/root instance -->
        <slot :form="this"></slot>
      </div>
    </form>
  </transition>
</template>

<script>
  import Vue from 'vue'
  import { Validator, default as VeeValidate } from 'vee-validate'
  import VueLogger from '@/util/logger'
  import ScrollTo from 'vue-scrollto'
  import serviceTitan from '@/mixins/serviceTitan'
  import getFields from '@/mixins/getFields'
  import sections from '@/mixins/form/sections'
  import fields from '@/mixins/form/fields'
  import validation from '@/mixins/form/validation'
  import submit from '@/mixins/form/submit'
  import autocomplete from '@/mixins/form/autocomplete'
  import packageInfo from '../../package'

  Vue.use(ScrollTo)
  Vue.use(VueLogger, { prefix: 'RF', dev: process.env.NODE_ENV === 'development' })
  Vue.use(VeeValidate, { events: '' })

  export default {
    name: 'RfForm',
    mixins: [serviceTitan, getFields, sections, fields, validation, submit, autocomplete],
    props: {
      formId: {
        type: String,
        required: true
      },
      requestKey: {
        type: String
      },
      // Note, this really ought to be called beforeSubmit but for backwards compatibility I don't want to change it.
      onSubmit: {
        type: Function,
        default () {
          this.submit()
        }
      },
      freeform: {
        type: Boolean,
        default: false
      },
      requireFieldIds: {
        type: Boolean,
        default: true
      },
      afterSubmit: {
        type: Function,
        default: null
      },
      serviceTitan: {
        type: Boolean,
        default: false
      },
      customSubmit: {
        type: Function,
        default: null
      },
      disableEnterKeySectionNav: {
        type: Boolean,
        default: false
      },
      enableErrorTracking: {
        type: Boolean,
        default: false
      },
      redirectTo: {
        type: String,
        required: false,
        default: ''
      },
      redirect: {
        type: Boolean,
        required: false,
        default: true
      },
      submitTo: {
        type: String,
        required: false,
        default: '/rocket-forms/submission'
      },
      autocomplete: {
        type: Object,
        default () {
          return {}
        },
        validator (value) {
          if (typeof value.fields === 'undefined' || (typeof value.fields !== 'undefined' && !value.fields.length)) {
            return true
          }

          // Ensure API key exists
          if (typeof value.apiKey === 'undefined') {
            return false
          }

          // Ensure config items at least have a 'main' propery
          return value.fields.every(function (config) {
            return typeof config.main !== 'undefined'
          })
        }
      }
    },

    // ////////////////////////////////////////////////////
    // Data
    // ////////////////////////////////////////////////////

    data () {
      return {
        fieldData: {},
        sections: [],
        activeSectionIndex: null,
        request: {}, // We'll store the xhr request here
        response: {}, // And the response
        state: {
          busy: false,
          form: {
            submitRequested: false,
            submitted: false
          },
          payment: {
            submitted: false
          }
        }
      }
    },

    // ////////////////////////////////////////////////////
    // Hooks
    // ////////////////////////////////////////////////////

    created () {
      // Static properties that don't need to be observed
      this.componentType = 'rf-form'
      this.version = packageInfo.version

      // If error tracking enabled, make sure global error tracker object
      // (e.g. Rollbar) is present
      if (this.enableErrorTracking && typeof Rollbar === 'undefined') {
        this.$error('Error tracking enabled but no global error tracker found.')
      } else if (this.enableErrorTracking) {
        this.$info('Sending form errors to Rollbar.')
      }

      // Bind events
      this.$on('field:id-changed', (name, newVal, oldVal) => { this.fieldData[name].id = newVal })
      this.$on('field:added', this._onFieldAdded)
      this.$on('field:changed', (name, newVal, oldVal) => { this.fieldData[name].value = newVal })
      this.$on('field:destroyed', this._onFieldDestroyed)
      this.$on('section:error', this.scrollToFirstError)
      this.$on('section:enter-key', this._onEnterKey)
      this.$on('section:added', this._onSectionAdded)
      this.$on('section:nav-next', this._onSectionNavNext)
      this.$on('section:nav-prev', this._onSectionNavPrev)
      this.$on('section:destroyed', this._onSectionDestroyed)

      // Add our custom vee-validate rule for components that need to have a value
      // that is explicity set by the user rather than whatever the default may be.
      // e.g. the Range component whose value cannot be null but we may neverless
      // want to "require" the user to interact with.
      Validator.extend('dirty', {
        getMessage: field => 'You must select a value for the ' + field + ' field.',
        validate: (value, [fieldName]) => {
          // This needs to be wrapped in a promise to give the dirty property on
          // the field vm time to update. Or something like that. W/o this you
          // have to update a field twice in order for an rf-section to recognize it as valid
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              /*
              Note, this validate method by default is (unfortunately) not passed any other data
              besides the value being validated (not the field name, etc). So in order
              to check if the field is dirty, we need to supply the field name as an argument
              to the validation rule, e.g. `validation="required_and_dirty:myField"`. It's not
              ideal.
              */
              let fieldVm = this.field(fieldName)
              if (!fieldVm) {
                this.$warn(`Invalid field name "${fieldName}" supplied with "dirty" validation rule`)
              }
              // Note, we are using the built-in `dirty` property on the fields object provided by vee-validate,
              // rather than using our own custom `isDirty` computed prop. Why? Because we kind of artificially
              // manipulate that our isDirty prop in order to force the validation UI to show. See how we manually set isDirty = true
              // in validate.js when forcing validation UI. Not relying on that here keeps it a bit more clean.
              resolve(fieldVm && !!value && (fieldVm.fields[fieldName].dirty || fieldVm.initialValue !== null))
            })
          })
        }
      })
    },

    mounted () {
      // Log version
      this.$log('Version:', this.version)

      // Prevent default spacebar behavior for certain elements
      // Note: this has to be attached to window; it's not enough to
      // stopPropagation() or preventDefault() in the components
      window.addEventListener('keydown', function (e) {
        if (e.keyCode === 32 && /(rf-checkbox|rf-radio|rf-cell|rf-next|rf-prev)/.test(e.target.className)) {
          e.preventDefault()
        }
      })

      // Add ready class to wrapper
      // For some reason the CSS transitions don't work w/o the setTimeout. I
      // think because Vue re-mounts the root element which resets transitions
      setTimeout(function () {
        // Add ready class
        // Beware browser limitations http://caniuse.com/#feat=classlist
        this.$root.$el.classList.add('rf-wrapper-ready')
        // This isn't strictly necessary, but once we 'hide' the loading dots with opacity: 0, they're still actually there,
        // animating away...just seems like a waste to leave them there and make the browser animate them. So we'll remove
        // them entirely after a bit.
        setTimeout(function () {
          this.$root.$el.classList.add('rf-wrapper-hide-loading')
        }.bind(this), 3000)
      }.bind(this))

      // Check for field IDs
      if (this.requireFieldIds) {
        this.getFields().filter(vm => !vm.fieldId).forEach(vm => this.$error('Missing a field ID:', vm.name))
      }
    },

    // ////////////////////////////////////////////////////
    // Methods
    // ////////////////////////////////////////////////////

    methods: {

      /**
       * Report error to rollbar
       * @param   {string}  level
       * @param   {string}  message
       * @param   {object}  payload
       */
      _report (level, message, payload) {
        if (this.enableErrorTracking && typeof Rollbar !== 'undefined') {
          Rollbar[level](message, payload)
        }
      },

      /**
       * Handle enter key behavior
       * @param   {Vue}  sectionVm
       * @param   {Object}  e
       */
      _onEnterKey (sectionVm, e) {
        // Only handle if we're not in a textarea; we leave default enter key behavior there
        // Also since we're binding both keyup and keydown, only run on keydown
        if (e.target.tagName !== 'TEXTAREA' && e.type === 'keydown') {
          e.preventDefault()
          e.stopPropagation()
          // Submit form if:
          // - Form has no sections OR
          // - Last section is active when enter key pressed
          if (!this.sections.length || (sectionVm && sectionVm._uid === this.sections[this.sections.length - 1]._uid)) {
            this.onSubmit()
            // Otherwise, fire section:nav-next event
          } else if (!this.disableEnterKeySectionNav) {
            this.$emit('section:nav-next', sectionVm, e)
          }
        }
      },

      /**
       * Creates the field data to be sent to the API
       * @return  {object}
       */
      _prepareFieldData () {
        var fields = []
        Object.keys(this.fieldData).forEach(key => {
          // Build a new variable rather than pushing the fieldData object, because
          // the fieldData object is reactive; we just want a plain object
          let field = {
            id: this.fieldData[key].id,
            name: this.fieldData[key].name,
            // value: this._prepareFieldValue(this.fieldData[key].value),
            // Field VM is responsible for returning its metadata and prepared value
            value: this.field(key).payloadValue,
            meta: this.field(key).meta
          }
          // Add only if field has a value
          if (field.value) {
            fields.push(field)
          }
        })

        return fields
      },

      /**
       * Set form state
       * @param  {string}  state
       */
      _setState (state) {
        switch (state) {
          case 'default':
            this.state.busy = false
            this.state.form.submitted = false
            break
          case 'busy':
            this.state.busy = true
            break
          case 'submit-request':
            this.state.form.submitRequested = true
            break
          case 'form-submitted':
            this.state.form.submitted = true
            this.state.busy = false
            break
        }
      }
    }
  }
</script>

<style lang='scss'>
  // This serves as the entry point for all our styles
  @import './scss/main';
</style>



// WEBPACK FOOTER //
// RfForm.vue?10153032